import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";

export type OldestUnreviewedStatusColor = "green" | "amber" | "red";

interface UseOldestUnreviewedAgeOptions {
  customerIds?: string[];
  dateFrom?: string;
  dateTo?: string;
}

interface OldestUnreviewedAgeResult {
  statusColor: OldestUnreviewedStatusColor;
  days: number;
  oldestDate: string | null;
}

export function useOldestUnreviewedAge(options?: UseOldestUnreviewedAgeOptions) {
  const customerIds = options?.customerIds;
  const dateFrom = options?.dateFrom;
  const dateTo = options?.dateTo;

  const query = useQuery<OldestUnreviewedAgeResult>({
    queryKey: ["oldest-unreviewed-age", customerIds?.join(","), dateFrom, dateTo],
    queryFn: async () => {
      let q = supabase
        .from("encounter_results_unreviewed")
        .select("observation_datetime")
        .order("observation_datetime", { ascending: true })
        .limit(1);

      if (customerIds && customerIds.length > 0) {
        q = q.in("customer_id", customerIds);
      }
      if (dateFrom) {
        q = q.gte("observation_datetime", dateFrom);
      }
      if (dateTo) {
        q = q.lte("observation_datetime", dateTo);
      }

      const { data, error } = await q;
      if (error) throw error;

      const oldest = data?.[0]?.observation_datetime as string | undefined;
      if (!oldest) {
        return { statusColor: "green", days: 0, oldestDate: null };
      }

      const nowMs = Date.now();
      const oldestMs = new Date(oldest).getTime();
      const days = Math.max(0, Math.floor((nowMs - oldestMs) / 86_400_000));

      const statusColor: OldestUnreviewedStatusColor = days <= 3 ? "green" : days <= 7 ? "amber" : "red";

      return { statusColor, days, oldestDate: oldest };
    },
    staleTime: 30000,
    gcTime: 5 * 60 * 1000,
  });

  return {
    statusColor: query.data?.statusColor ?? "green",
    days: query.data?.days ?? 0,
    oldestDate: query.data?.oldestDate ?? null,
    isLoading: query.isLoading,
    error: query.error as Error | null,
    refetch: query.refetch,
  };
}
